import type {
  ProjectCreationData, Category, FolderTreeNodeStats, Participant, Invitation, Project, ProjectUpdateData,
  ShareParams
} from '@bimdata/definitions/dto';
import api from './api';
import { AccessLevel } from '@bimdata/definitions/const';
import castDates from '@bimdata/client/utils/cast-dates';

async function create(body: ProjectCreationData): Promise<Project> {
  const { data } = await api.post<Project>('/project', body);
  return castDates<Project>(data, [ 'created', 'updated' ]);
}

async function list(): Promise<Project[]> {
  const { data } = await api.get<Project[]>('/project');
  return data.map((project) => castDates<Project>(project, [ 'created', 'updated' ]));
}

async function categories(id: string, q?: string, accessToken?: string): Promise<Category[]> {
  const { data } = await api.get<Category[]>(
    `/project/${id}/category?q=${q}`,
    { accessToken }
  );
  return data;
}

async function category(id: string, categoryId: string): Promise<Category> {
  const { data } = await api.get<Category>(`/project/${id}/category/${categoryId}`);
  return data;
}

async function classification(id: string, category: number, accessToken?: string): Promise<FolderTreeNodeStats[]> {
  const { data } = await api.post<FolderTreeNodeStats[]>(
    `/project/${id}/folder/stats`,
    { category: [category] },
    { accessToken }
  );
  return data;
}

async function get(id: string, accessToken?: string): Promise<Project> {
  const { data } = await api.get<Project>(
    `/project/${id}`,
    { accessToken }
  );
  return castDates<Project>(data, [ 'created', 'updated' ]);
}

async function getLogo(id: string, logo: number): Promise<string | null> {
  try {
    const { data } = await api.get(`/project/${id}/logo?v=${logo}`, { responseType: 'blob' });
    return URL.createObjectURL(data);
  } catch (error) {
    if (error.code !== 404) {
      throw error;
    }
  }
  return null;
}

async function getThumbnail(hash: string): Promise<string | null> {
  try {
    const { data } = await api.get(`/files/${hash}`, { responseType: 'blob' });
    return URL.createObjectURL(data);
  } catch (error) {
    if (error.code !== 404) {
      throw error;
    }
  }
  return null;
}

async function saveThumbnail(id: string, hash: string, thumbnail: Blob): Promise<void> {
  const fd = new FormData();
  fd.append('file', thumbnail);
  fd.set('project', id);
  await api.post(
    `/files/VIEWERSCREENSHOT/${hash}`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
}

async function setLogo(id: string, file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  await api.post(
    `/project/${id}/logo`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return URL.createObjectURL(file);
}

async function deleteLogo(id: string): Promise<void> {
  await api.delete(`/project/${id}/logo`);
}

async function update(id: string, body: ProjectUpdateData): Promise<void> {
  await api.put(`/project/${id}`, body);
}

async function remove(id: string): Promise<void> {
  await api.delete(`/project/${id}`);
}

async function invitations(id: string): Promise<Array<Invitation>> {
  const { data } = await api.get<Array<Invitation>>(`/project/${id}/invitations`);
  return data;
}

async function participants(id: string): Promise<Array<Participant>> {
  const { data } = await api.get(`/project/${id}/participants`);
  return Array.isArray(data) ? data.map((p) => castDates(p, ['since'])) : data;
}

async function excludeParticipant(projectId: string, participant: string): Promise<void> {
  await api.delete(`/project/${projectId}/participants/${participant}`);
}

async function editParticipant(projectId: string, participant: string, access: AccessLevel): Promise<void> {
  await api.put(`/project/${projectId}/participants/${participant}`, { access });
}

async function leave(projectId: string): Promise<void> {
  await api.delete(`/project/${projectId}/participant`);
}

async function setParticipantBlocked(projectId: string, participant: string, blocked: boolean): Promise<void> {
  await api.put(`/project/${projectId}/participants/${participant}/set-blocked`, { blocked });
}

async function share(projectId: string, body?: ShareParams): Promise<string> {
  const { data } = await api.post<string>(`/project/${projectId}/share`, body);
  return data;
}

async function conceal(projectId: string): Promise<void> {
  await api.delete(`/project/${projectId}/share`);
}

async function getShareCode(projectId: string): Promise<string> {
  const { data } = await api.get<string>(`/project/${projectId}/share/code`);
  return data;
}

export {
  create,
  list,
  get,
  categories,
  category,
  classification,
  getLogo,
  setLogo,
  update,
  remove,
  invitations,
  participants,
  excludeParticipant,
  editParticipant,
  leave,
  deleteLogo,
  setParticipantBlocked,
  saveThumbnail,
  getThumbnail,
  share,
  conceal,
  getShareCode,
};
